import type { CricketStats, ItemKind, MatchConfig, ResourceKind, Vec2 } from "@cc/protocol";
import { TICKS_PER_SECOND } from "@cc/protocol";

export const DEFAULT_STATS: CricketStats = {
  /**
   * 300 HP. Matches are 90 decision steps and ranked by (crystal score +
   * remaining HP), so attackers who chip the opponent are rewarded even without
   * a kill, while farmers still bank crystals. See `scripts/sweep.ts` to
   * re-derive this.
   */
  maxHp: 300,
  speed: 3.2,
  /**
   * 34 rather than the old 22. Measured: at 22 the two crickets often farmed
   * opposite halves and rarely met; a larger disc raises interaction rate without
   * collapsing the information-asymmetry design (still ~1/3 of a 100-unit map).
   */
  visionRadius: 34,
  attackDamage: 12,
  attackRange: 3.5,
  attackCooldownTicks: 8,
  collectTicks: 6,
  collectRange: 2.5,
  /** Long enough that a hit visibly separates the pair instead of a micro-nudge. */
  knockbackTicks: 8,
  knockbackSpeedMul: 2.4,
};

/** Master tuning table for the arena. All gameplay numbers live here. */
export const ARENA = {
  /**
   * Radius at which a *moving* cricket physically sets off a trap.
   *
   * Traps are landmines, not locked chests: they fire when you walk onto them,
   * not when you try to collect them. This is deliberate and was measured into
   * place. An earlier design triggered traps only via COLLECT, but passive
   * proximity detection then always defused them first, and across hundreds of
   * simulated matches the trap trigger count was exactly zero — the single most
   * entertaining beat in the design doc never once occurred on screen.
   */
  trapContactRadius: 1.5,
  /**
   * Damage a trap deals. Tuned against the 90-second match: enough to punish
   * greed without ending every contest before the whistle.
   */
  trapDamage: 28,
  /** Score lost by setting off a trap. */
  trapScorePenalty: 10,
  /**
   * Tick from which traps begin spawning. Opening seconds are for racing
   * resources; traps arrive once a pattern has been established.
   */
  trapUnlockTick: 100,
  /**
   * Traps stop spawning this many ticks before the end, so the closing stretch
   * is a clean fight for the win instead of a coin-flip minefield.
   */
  trapCutoffTicks: 100,
  /**
   * Radius at which a cricket *senses* an unidentified trap nearby.
   *
   * Much larger than the contact radius, but still usually too late to dodge at
   * full speed: a cricket covers 3.2 units per tick. So this is a warning, not
   * a save. The real counter-play is SCOUT, which reaches 12 units out and is
   * the only way to learn which crystal is a trap *before* committing.
   */
  trapDetectRadius: 6,
  /** Radius of the SCOUT pulse that identifies resources (and traps) in an area. */
  scoutRadius: 12,
  /** SCOUT costs this many ticks of standing still, creating a real tradeoff. */
  scoutTicks: 3,
  /** Fraction of incoming damage prevented while DEFEND is active. */
  defendDamageReduction: 0.5,
  /** Maximum simultaneous resources on the map. */
  maxResources: 11,
  /** A new resource is attempted every N ticks when below max. */
  spawnIntervalTicks: 10,
  /** Uncollected resources expire after this many ticks. */
  resourceLifetimeTicks: 140,
  /** How far from spawn a cricket may wander. */
  margin: 3,
  /** Bonus score awarded for eliminating the opponent. */
  eliminationBonus: 25,

  /* ---- Combat items (map pickups + crystal exchange) ---- */

  /** AoE bomb blast radius. */
  bombRadius: 14,
  /** Damage dealt to the opponent inside the blast (self is immune). */
  bombDamage: 40,
  /** Extra knockback ticks applied to a bomb victim. */
  bombKnockbackTicks: 10,

  /** Spinning-blade aura duration once activated. */
  bladeDurationTicks: 45,
  /** Damage per tick while an enemy is inside the blade radius. */
  bladeDamagePerTick: 4,
  /** Radius of the spinning-blade aura. */
  bladeRange: 5.5,

  /** Crystal-score cost to buy an item via EXCHANGE. */
  exchangeCost: { bomb: 25, blade: 30 } as const satisfies Record<ItemKind, number>,

  /** FLEE sprint duration. */
  fleeDurationTicks: 16,
  /** Speed multiplier while fleeing. */
  fleeSpeedMul: 1.7,
} as const;

export interface ResourceTemplate {
  readonly kind: ResourceKind;
  readonly value: number;
  /** Relative spawn weight. */
  readonly weight: number;
  /** Traps are disguised as crystals until identified. */
  readonly disguised: boolean;
}

export const RESOURCE_TABLE: readonly ResourceTemplate[] = [
  { kind: "crystal", value: 10, weight: 40, disguised: false },
  { kind: "energy", value: 25, weight: 14, disguised: false },
  { kind: "shield", value: 8, weight: 12, disguised: false },
  { kind: "bomb", value: 5, weight: 12, disguised: false },
  { kind: "blade", value: 5, weight: 10, disguised: false },
  { kind: "trap", value: 30, weight: 12, disguised: true },
];

export const SHIELD_HEAL = 15;

/** Traps masquerade as high-value crystals, which is what makes them tempting. */
export const TRAP_BAIT_VALUE = 30;

/**
 * Default match length in *decision steps*.
 *
 * Arena time is deliberately not wall-clock. One abstract "second" is one
 * decision beat: both crickets observe, choose one action, then the engine
 * advances. A match is ninety such beats, then scores are compared. Live-model
 * latency is absorbed by holding the world on the decision tick — a slow model
 * does not lose arena time to a fast one. That is what makes AI vs AI fair.
 */
export const DEFAULT_MATCH_STEPS = 90;

/**
 * Alias kept for call sites that still say "seconds". Same number as
 * {@link DEFAULT_MATCH_STEPS}: abstract seconds, not wall-clock seconds.
 */
export const DEFAULT_MATCH_SECONDS = DEFAULT_MATCH_STEPS;

/** How many simulation ticks sit between consecutive decisions (one abstract second). */
export const DEFAULT_DECISION_INTERVAL_TICKS = TICKS_PER_SECOND;

/**
 * Decision cadence for matches that include a live model.
 *
 * Same grid as heuristic bots: one decision per abstract second. The browser
 * client holds the world until every due model has answered, so provider
 * latency stretches wall-clock time but never the number of decisions or the
 * arena physics between them. Fairness is step count, not response speed.
 */
export const AI_DECISION_INTERVAL_TICKS = DEFAULT_DECISION_INTERVAL_TICKS;

/** Match length in ticks for a given step count and decision interval. */
export function durationTicksForSteps(
  steps: number = DEFAULT_MATCH_STEPS,
  intervalTicks: number = DEFAULT_DECISION_INTERVAL_TICKS,
): number {
  return Math.max(1, Math.round(steps) * Math.max(1, Math.round(intervalTicks)));
}

export function createDefaultConfig(overrides: Partial<MatchConfig> = {}): MatchConfig {
  return {
    seed: 1337,
    arenaSize: 100,
    durationTicks: durationTicksForSteps(),
    agentIds: ["a", "b"],
    stats: DEFAULT_STATS,
    decisionIntervalTicks: DEFAULT_DECISION_INTERVAL_TICKS,
    // Resource + HP war by default; see MatchConfig.eliminationEndsMatch.
    eliminationEndsMatch: false,
    ...overrides,
  };
}

/**
 * Agents do not all think on the same tick. Offsetting each agent's decision
 * phase by a seed-derived amount stops the two crickets from moving in
 * lockstep, which reads as much more lifelike on screen.
 *
 * Live-model matches pass `aligned: true` so both slots decide together: the
 * spectator sees a fair "both are thinking" beat, and the client can hold the
 * world until every due model has answered the *same* board state.
 */
export function decisionPhase(
  seed: number,
  slot: number,
  intervalTicks: number,
  opts: { readonly aligned?: boolean } = {},
): number {
  if (opts.aligned || intervalTicks <= 1) return 0;
  const spread = Math.floor(intervalTicks / 2);
  return (Math.abs(seed + slot * 7 + slot) % spread) % intervalTicks;
}

export function clampToArena(p: Vec2, arenaSize: number, margin: number): Vec2 {
  const lo = margin;
  const hi = arenaSize - margin;
  return [Math.min(hi, Math.max(lo, p[0])), Math.min(hi, Math.max(lo, p[1]))];
}

export function spawnPositions(arenaSize: number): readonly [Vec2, Vec2] {
  const inset = arenaSize * 0.22;
  return [
    [inset, arenaSize - inset],
    [arenaSize - inset, inset],
  ];
}

/** Ranking total used at the whistle and shown in the HUD. */
export function rankingTotal(score: number, hp: number): number {
  return score + Math.max(0, hp);
}
